import React, { useEffect, useMemo, useRef, useState } from "react";
import { Prompt, useHistory, useParams } from "react-router-dom";
import { Controlled as CodeMirror } from "react-codemirror2";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import throttle from "lodash/throttle";
import "codemirror/lib/codemirror.css";
import "codemirror/mode/markdown/markdown";
import {
  Alert,
  AppBar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Select,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import BuildIcon from "@mui/icons-material/Build";
import EditIcon from "@mui/icons-material/Edit";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import SyncIcon from "@mui/icons-material/Sync";
import SyncDisabledIcon from "@mui/icons-material/SyncDisabled";

import {
  fetchArticleContent,
  fetchArticleMeta,
  updateArticle,
  validateArticleDraft,
  uploadArticleImage,
  acquireArticleLock,
  releaseArticleLock,
} from "../api/marketing";
import { getErrorMessage } from "../api/generic";

// Mirrors publisher.py's WP_PREFIX: images are authored as
// `/wp-content/uploads/<key>` and only resolve on the live astro site,
// whose edge strips this prefix and serves `<key>` from R2. The authoring
// app has no such proxy, so the preview rewrites these to the R2 bucket's
// public URL directly; the stored markdown itself is left untouched.
const WP_UPLOADS_PREFIX = "/wp-content/uploads/";

function resolveImageSrc(src, r2PublicUrl) {
  if (!src || !r2PublicUrl || !src.startsWith(WP_UPLOADS_PREFIX)) return src;
  return `${r2PublicUrl.replace(/\/$/, "")}/${src.slice(
    WP_UPLOADS_PREFIX.length
  )}`;
}

// Block-level tags react-markdown's `sourcePos` option tags with a
// `data-sourcepos="<startLine>:<startCol>-<endLine>:<endCol>"` attribute.
// Only these are used as scroll-sync anchors, since matching on every
// inline element (links, emphasis, ...) would pick anchors too fine-grained
// to correspond to a distinct visual position in the preview.
const SCROLL_SYNC_SELECTOR = [
  "p",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "li",
  "blockquote",
  "pre",
  "table",
  "hr",
  "img",
]
  .map((tag) => `${tag}[data-sourcepos]`)
  .join(", ");

function getSourceStartLine(element) {
  const sourcepos = element.getAttribute("data-sourcepos");
  const line = sourcepos && Number.parseInt(sourcepos, 10);
  return Number.isNaN(line) ? null : line;
}

// `element.offsetTop` is relative to its nearest *positioned* ancestor, not
// to `container` (neither MUI's Paper nor the markdown content set
// `position: relative`, so that ancestor ends up being the page body) -
// this measures the offset relative to `container`'s own scroll area instead.
function offsetWithinContainer(element, container) {
  return (
    element.getBoundingClientRect().top -
    container.getBoundingClientRect().top +
    container.scrollTop
  );
}

// Each lock is held for a wall-clock cooldown (VS Code's markdown preview
// does the same, via `scrollDisabledCount` in its preview-src/index.ts)
// rather than a single requestAnimationFrame, because the echo it guards
// against isn't always same-frame: an image in the preview finishing
// loading (and reflowing everything below it) or the browser's own
// scroll-anchoring compensating for that fires a *native* scroll event
// whenever the network/layout happens to settle, which is routinely later
// than one frame after the write. A lock that lifts after one frame lets
// that late, unrelated scroll through as if the user had caused it.
const SYNC_COOLDOWN_MS = 200;

function suppressHandler(ref, timerRef) {
  ref.current = true;
  if (timerRef.current) {
    clearTimeout(timerRef.current);
  }
  timerRef.current = setTimeout(() => {
    ref.current = false;
  }, SYNC_COOLDOWN_MS);
}

// Only images ABOVE `target` can shift its top offset, so only those are
// worth waiting on - waiting on every image in the article (most of it
// already loaded, some of it far below and irrelevant to this sync) is
// what made source->preview scrolling feel occasionally laggy.
function runAfterPrecedingImagesLoaded(container, target, cb) {
  const pending = Array.from(container.querySelectorAll("img")).filter(
    (img) =>
      !img.complete &&
      target.compareDocumentPosition(img) & Node.DOCUMENT_POSITION_PRECEDING
  );
  if (pending.length === 0) {
    cb();
    return;
  }
  Promise.all(
    pending.map(
      (img) =>
        new Promise((resolve) => {
          img.addEventListener("load", resolve, { once: true });
          img.addEventListener("error", resolve, { once: true });
        })
    )
  ).then(cb);
}

// The featured-image preview shows up both in the article header and in the
// edit-metadata dialog, at different sizes and with a different fallback.
function FeaturedImageThumbnail({ src, r2PublicUrl, sizeClassName, fallback }) {
  return (
    <Box
      className={`${sizeClassName} dq-rounded dq-overflow-hidden dq-flex-shrink-0 dq-flex dq-items-center dq-justify-center`}
      sx={{ bgcolor: "action.hover" }}
    >
      {src ? (
        <Box
          component="img"
          src={resolveImageSrc(src, r2PublicUrl)}
          alt=""
          className="dq-w-full dq-h-full dq-object-cover"
        />
      ) : (
        fallback
      )}
    </Box>
  );
}

// The "pick an image file" control shows up both as the small "Insert
// image" toolbar button and the "Upload new image" dialog button; only the
// label/size/icon and what happens with the uploaded path differ.
function ImageUploadButton({
  label,
  uploading,
  disabled,
  size,
  startIcon,
  onChange,
}) {
  return (
    <Button
      component="label"
      size={size}
      startIcon={startIcon}
      disabled={uploading || disabled}
    >
      {uploading ? "Uploading..." : label}
      <input type="file" accept="image/*" hidden onChange={onChange} />
    </Button>
  );
}

export default function MarketingArticleEditor() {
  const history = useHistory();
  const { articleId } = useParams();

  const [currentArticleId, setCurrentArticleId] = useState(articleId);
  // Unique per mount: the edit lock is bound to this token, so a reload or
  // second tab of the same user takes the lock over and this one goes stale.
  const sessionIdRef = useRef(
    `${Date.now()}-${Math.random().toString(36).slice(2)}`
  );
  const [frontmatter, setFrontmatter] = useState(null);
  const [body, setBody] = useState("");
  const [meta, setMeta] = useState({
    authors: [],
    categories: [],
    r2_public_url: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [validating, setValidating] = useState(false);
  // { errors: string[], warnings: string[] } from the last validation; the
  // dialog is open whenever this is non-null.
  const [validation, setValidation] = useState(null);
  const [error, setError] = useState("");
  // Name of whoever else holds the edit lock; non-null means this editor is
  // open read-only.
  const [lockedBy, setLockedBy] = useState(null);
  const [success, setSuccess] = useState("");
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [metadataModalOpen, setMetadataModalOpen] = useState(false);
  const [previewVisible, setPreviewVisible] = useState(true);
  const [scrollSyncEnabled, setScrollSyncEnabled] = useState(true);
  const codeMirrorRef = useRef(null);
  const previewRef = useRef(null);
  const sourcePaneRef = useRef(null);
  // The scroll handlers are memoized once (see below) and only ever read
  // refs, so the on/off toggle has to reach them through a ref too - a plain
  // read of `scrollSyncEnabled` state would close over the value from that
  // first render and never see later toggles.
  const scrollSyncEnabledRef = useRef(true);
  // Two separate locks, not one shared flag: each guards only the *other*
  // pane's handler against the echo from our own write, so a pane's handler
  // never blocks itself from reacting to that same pane's own continued,
  // genuine scrolling (a single shared flag caused exactly that - syncing
  // preview scroll would suppress every following preview scroll tick for
  // the length of the cooldown, since handlePreviewScroll checked the same
  // flag it had just set, making the sync feel laggy/stuck).
  const suppressSourceHandlerRef = useRef(false);
  const suppressPreviewHandlerRef = useRef(false);
  const sourceCooldownTimerRef = useRef(null);
  const previewCooldownTimerRef = useRef(null);

  const toggleScrollSync = () => {
    setScrollSyncEnabled((current) => {
      const next = !current;
      scrollSyncEnabledRef.current = next;
      return next;
    });
  };

  useEffect(() => {
    if (!hasUnsavedChanges) return;
    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasUnsavedChanges]);

  useEffect(() => {
    const sessionId = sessionIdRef.current;
    let holdsLock = false;
    let unmounted = false;
    const release = () => {
      if (!holdsLock) return;
      holdsLock = false;
      releaseArticleLock(articleId, sessionId);
    };

    setLoading(true);
    // The lock is taken before loading anything: someone else holding it
    // means this page shows who, instead of an editor that can't save.
    const loadArticle = () => {
      fetchArticleContent(
        currentArticleId,
        (data) => {
          setFrontmatter(data.frontmatter);
          setBody(data.body);
          setLoading(false);
        },
        (error) => {
          setError(getErrorMessage(error));
          setLoading(false);
        }
      );
      fetchArticleMeta(
        (data) => setMeta(data),
        () => {} // Non-fatal: the form still works with free-text fallbacks.
      );
    };
    acquireArticleLock(
      articleId,
      sessionId,
      () => {
        holdsLock = true;
        if (unmounted) {
          // Left the page before the acquire came back: give it straight up.
          release();
          return;
        }
        loadArticle();
      },
      (error, details) => {
        if (details?.lockedBy) {
          // Reading needs no lock: show the article read-only in the normal
          // layout, with a banner naming the holder.
          setLockedBy(details.lockedBy);
          loadArticle();
          return;
        }
        setError(getErrorMessage(error));
        setLoading(false);
      }
    );

    // The lock never expires, so it is released only on a clean exit: in-app
    // navigation (after the unsaved-changes Prompt, if any) unmounts this
    // component, and `pagehide` covers closing the tab or leaving the site -
    // it fires only once the user has passed any beforeunload warning.
    window.addEventListener("pagehide", release);
    return () => {
      unmounted = true;
      window.removeEventListener("pagehide", release);
      release();
    };
    // Only ever reload from the article this editor was opened for; a
    // successful save updates state locally instead of refetching.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFieldChange = (field, value) => {
    setFrontmatter((current) => ({ ...current, [field]: value }));
    setHasUnsavedChanges(true);
  };

  const handleBodyChange = (value) => {
    setBody(value);
    setHasUnsavedChanges(true);
  };

  // Shared by every "an API call just failed" callback: stop whatever
  // spinner was running for it and surface the error banner.
  const handleApiError = (setBusy) => (error) => {
    setBusy(false);
    setError(getErrorMessage(error));
  };

  const handleImageUpload = (file, onUploaded) => {
    if (!file) return;
    setUploadingImage(true);
    setError("");
    uploadArticleImage(
      currentArticleId,
      sessionIdRef.current,
      file,
      (result) => {
        setUploadingImage(false);
        onUploaded(result.path);
      },
      handleApiError(setUploadingImage)
    );
  };

  const handleImageInputChange = (onUploaded) => (event) => {
    handleImageUpload(event.target.files[0], onUploaded);
    event.target.value = "";
  };

  const insertImageAtCursor = (path) => {
    const editor = codeMirrorRef.current;
    const html = `<img src="${path}" alt="">`;
    if (!editor) {
      handleBodyChange(body + html);
      return;
    }
    editor.getDoc().replaceRange(html, editor.getDoc().getCursor());
  };

  // Keeps the preview's scroll position matched to the source editor's, by
  // line rather than by scroll percentage: this content is image-heavy, and
  // an image can occupy far more (or less) rendered height than its single
  // line of markdown source, so a percentage-based sync drifts badly.
  //
  // `.CodeMirror { height: auto }` (see App.css) makes the editor grow to
  // fit its content instead of scrolling internally, so the pane that
  // actually scrolls is the wrapping Paper (`sourcePaneRef`) - CodeMirror's
  // own `scroll` event never fires here. `editor.lineAtHeight`/`heightAtLine`
  // still give the right coordinates (CodeMirror measures "local" position
  // from the top of its content either way); we just read/set the *wrapper's*
  // scrollTop instead of the editor's own (non-functional) internal scroll.
  //
  // Throttled (like VS Code's own preview scroll listeners) so a burst of
  // native scroll events collapses into one handled tick instead of each one
  // separately racing the sync guard.
  const handleSourceScroll = useMemo(
    () =>
      throttle(() => {
        if (suppressSourceHandlerRef.current || !scrollSyncEnabledRef.current)
          return;
        const editor = codeMirrorRef.current;
        const previewContainer = previewRef.current;
        const sourcePane = sourcePaneRef.current;
        if (!editor || !previewContainer || !sourcePane) return;

        const topLine = editor.lineAtHeight(sourcePane.scrollTop, "local") + 1;
        const blocks = previewContainer.querySelectorAll(SCROLL_SYNC_SELECTOR);
        let target = null;
        for (const block of blocks) {
          const line = getSourceStartLine(block);
          if (line === null || line > topLine) break;
          target = block;
        }
        if (!target) return;

        suppressHandler(suppressPreviewHandlerRef, previewCooldownTimerRef);
        runAfterPrecedingImagesLoaded(previewContainer, target, () => {
          previewContainer.scrollTop = offsetWithinContainer(
            target,
            previewContainer
          );
        });
      }, 50),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const handlePreviewScroll = useMemo(
    () =>
      throttle(() => {
        if (suppressPreviewHandlerRef.current || !scrollSyncEnabledRef.current)
          return;
        const editor = codeMirrorRef.current;
        const sourcePane = sourcePaneRef.current;
        const container = previewRef.current;
        if (!editor || !sourcePane || !container) return;

        const blocks = container.querySelectorAll(SCROLL_SYNC_SELECTOR);
        let target = null;
        for (const block of blocks) {
          if (offsetWithinContainer(block, container) > container.scrollTop + 1)
            break;
          target = block;
        }
        if (!target) return;
        const line = getSourceStartLine(target);
        if (line === null) return;

        suppressHandler(suppressSourceHandlerRef, sourceCooldownTimerRef);
        sourcePane.scrollTop = editor.heightAtLine(
          Math.max(line - 1, 0),
          "local"
        );
      }, 50),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(() => {
    return () => {
      handleSourceScroll.cancel();
      handlePreviewScroll.cancel();
      // These hold a timer id that's reset on every scroll tick, not a DOM
      // node, so reading `.current` here (the latest id) is intentional -
      // capturing it into a variable at effect-setup time would instead
      // clear whatever timer was pending at mount, not at unmount.
      if (sourceCooldownTimerRef.current) {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        clearTimeout(sourceCooldownTimerRef.current);
      }
      if (previewCooldownTimerRef.current) {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        clearTimeout(previewCooldownTimerRef.current);
      }
    };
  }, [handleSourceScroll, handlePreviewScroll]);

  const handleSave = () => {
    setSaving(true);
    setError("");
    setSuccess("");
    updateArticle(
      currentArticleId,
      sessionIdRef.current,
      frontmatter,
      body,
      (result) => {
        setSaving(false);
        setSuccess("Saved to stage.");
        setHasUnsavedChanges(false);
        setCurrentArticleId(result.id);
        history.replace(`/marketing/article/${result.id}`);
      },
      handleApiError(setSaving)
    );
  };

  const runValidation = (autofix = false) => {
    setValidating(true);
    setError("");
    validateArticleDraft(
      currentArticleId,
      frontmatter,
      body,
      autofix,
      (result) => {
        setValidating(false);
        if (autofix) handleBodyChange(result.body);
        setValidation(result);
      },
      handleApiError(setValidating)
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" my={8}>
        <CircularProgress />
      </Box>
    );
  }

  if (!frontmatter) {
    return (
      <Box m={4}>
        <Alert severity="error">{error || "Article not found."}</Alert>
      </Box>
    );
  }

  return (
    <Box className="dq-h-full dq-flex dq-flex-col dq-overflow-hidden">
      <Prompt
        when={hasUnsavedChanges}
        message="You have unsaved changes that will be lost if you leave this page. Continue?"
      />
      <AppBar
        position="static"
        color="transparent"
        variant="outlined"
        className="dq-flex-shrink-0"
      >
        <Toolbar className="dq-gap-4">
          <IconButton edge="start" onClick={() => history.push("/marketing")}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" className="dq-flex-grow">
            Edit article — {frontmatter.slug}
          </Typography>
          <Button
            startIcon={
              previewVisible ? <VisibilityOffIcon /> : <VisibilityIcon />
            }
            onClick={() => setPreviewVisible((current) => !current)}
            variant="outlined"
          >
            {previewVisible ? "Hide preview" : "Show preview"}
          </Button>
          <Button
            startIcon={<FactCheckIcon />}
            variant="outlined"
            disabled={validating}
            onClick={() => runValidation()}
          >
            {validating ? "Validating..." : "Validate"}
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<SaveIcon />}
            disabled={saving || Boolean(lockedBy)}
            onClick={handleSave}
          >
            {saving ? "Saving..." : "Save to stage"}
          </Button>
        </Toolbar>
      </AppBar>

      <Box className="dq-flex-1 dq-min-h-0 dq-flex dq-flex-col dq-p-4">
        {lockedBy && (
          <Alert severity="warning" className="dq-mb-4 dq-flex-shrink-0">
            This article is being edited by {lockedBy}. You can read it here,
            but it is read-only until they're done.
          </Alert>
        )}
        {error && (
          <Alert severity="error" className="dq-mb-4 dq-flex-shrink-0">
            {error}
          </Alert>
        )}
        {success && (
          <Alert
            severity="success"
            className="dq-mb-4 dq-flex-shrink-0"
            onClose={() => setSuccess("")}
          >
            {success}
          </Alert>
        )}

        <Paper
          variant="outlined"
          className="dq-p-4 dq-mb-4 dq-flex-shrink-0 dq-flex dq-items-center dq-gap-4"
        >
          <FeaturedImageThumbnail
            src={frontmatter.featured_image}
            r2PublicUrl={meta.r2_public_url}
            sizeClassName="dq-w-16 dq-h-16"
            fallback={<AddPhotoAlternateIcon color="disabled" />}
          />
          <Box className="dq-flex-grow dq-min-w-0">
            <Typography variant="subtitle1" noWrap>
              {frontmatter.title || "Untitled"}
            </Typography>
            <Typography variant="body2" color="text.secondary" noWrap>
              /{frontmatter.slug}
            </Typography>
            <Box className="dq-flex dq-flex-wrap dq-gap-1 dq-mt-1">
              {frontmatter.author && (
                <Chip size="small" label={frontmatter.author} />
              )}
              {frontmatter.date && (
                <Chip
                  size="small"
                  variant="outlined"
                  label={frontmatter.date}
                />
              )}
              {(frontmatter.categories || []).map((category) => (
                <Chip
                  key={category}
                  size="small"
                  variant="outlined"
                  label={category}
                />
              ))}
            </Box>
          </Box>
          <Button
            startIcon={<EditIcon />}
            disabled={Boolean(lockedBy)}
            onClick={() => setMetadataModalOpen(true)}
          >
            Edit Metadata
          </Button>
        </Paper>

        <Dialog
          open={metadataModalOpen}
          onClose={() => setMetadataModalOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Edit metadata</DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2}>
              <Grid item xs={12} md={9}>
                <TextField
                  fullWidth
                  label="Title"
                  value={frontmatter.title || ""}
                  onChange={(event) =>
                    handleFieldChange("title", event.target.value)
                  }
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Slug"
                  value={frontmatter.slug || ""}
                  disabled
                  helperText="Change requires a new upload"
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary" mb={1}>
                  Featured image
                </Typography>
                <Box className="dq-flex dq-gap-4 dq-items-start">
                  <FeaturedImageThumbnail
                    src={frontmatter.featured_image}
                    r2PublicUrl={meta.r2_public_url}
                    sizeClassName="dq-w-60 dq-h-40"
                    fallback={
                      <Typography variant="caption" color="text.secondary">
                        No image
                      </Typography>
                    }
                  />
                  <Box className="dq-flex-grow">
                    <TextField
                      fullWidth
                      label="Image path"
                      value={frontmatter.featured_image || ""}
                      onChange={(event) =>
                        handleFieldChange("featured_image", event.target.value)
                      }
                      className="dq-mb-2"
                    />
                    <ImageUploadButton
                      label="Upload new image"
                      uploading={uploadingImage}
                      startIcon={<AddPhotoAlternateIcon />}
                      onChange={handleImageInputChange((path) =>
                        handleFieldChange("featured_image", path)
                      )}
                    />
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  label="Description"
                  value={frontmatter.description || ""}
                  helperText={`${
                    (frontmatter.description || "").length
                  } characters (120-160)`}
                  onChange={(event) =>
                    handleFieldChange("description", event.target.value)
                  }
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Date"
                  value={frontmatter.date || ""}
                  helperText="YYYY-MM-DDTHH:MM:SS"
                  onChange={(event) =>
                    handleFieldChange("date", event.target.value)
                  }
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Select
                  fullWidth
                  displayEmpty
                  value={frontmatter.author || ""}
                  onChange={(event) =>
                    handleFieldChange("author", event.target.value)
                  }
                >
                  <MenuItem value="" disabled>
                    Author
                  </MenuItem>
                  {meta.authors.map((author) => (
                    <MenuItem key={author} value={author}>
                      {author}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>
              <Grid item xs={12} md={4}>
                <Select
                  fullWidth
                  multiple
                  displayEmpty
                  value={frontmatter.categories || []}
                  onChange={(event) =>
                    handleFieldChange("categories", event.target.value)
                  }
                  renderValue={(selected) =>
                    selected.length ? (
                      <Box className="dq-flex dq-flex-wrap dq-gap-1">
                        {selected.map((value) => (
                          <Chip key={value} label={value} size="small" />
                        ))}
                      </Box>
                    ) : (
                      "Categories"
                    )
                  }
                >
                  {meta.categories.map((category) => (
                    <MenuItem key={category} value={category}>
                      {category}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setMetadataModalOpen(false)}>Done</Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={Boolean(validation)}
          onClose={() => setValidation(null)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Validation</DialogTitle>
          <DialogContent dividers>
            {validation?.errors.length === 0 &&
            validation?.warnings.length === 0 ? (
              <Alert severity="success">No issues found.</Alert>
            ) : (
              <Box className="dq-flex dq-flex-col dq-gap-2">
                {validation?.errors.map((message) => (
                  <Alert key={message} severity="error">
                    {message}
                  </Alert>
                ))}
                {validation?.warnings.map((message) => (
                  <Alert key={message} severity="warning">
                    {message}
                  </Alert>
                ))}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            {validation?.warnings.length > 0 && (
              <Button
                startIcon={<BuildIcon />}
                disabled={validating || Boolean(lockedBy)}
                onClick={() => runValidation(true)}
              >
                Automatically fix
              </Button>
            )}
            <Button onClick={() => setValidation(null)}>Close</Button>
          </DialogActions>
        </Dialog>

        <Box
          className="dq-flex-1 dq-min-h-0 dq-flex dq-gap-4"
          sx={{
            // Tailwind's default `md:` breakpoint (768px) doesn't match
            // MUI's `md` (900px), so this one stays on sx rather than
            // silently shifting where source/preview stack vs sit side by
            // side.
            flexDirection: { xs: "column", md: "row" },
          }}
        >
          <Box className="dq-flex-1 dq-min-h-0 dq-min-w-0 dq-flex dq-flex-col">
            <Box className="dq-flex dq-items-center dq-justify-between dq-mb-2 dq-flex-shrink-0">
              <Typography variant="subtitle2" color="text.secondary">
                Source
              </Typography>
              <ImageUploadButton
                label="Insert image"
                uploading={uploadingImage}
                disabled={Boolean(lockedBy)}
                size="small"
                startIcon={<AddPhotoAlternateIcon fontSize="small" />}
                onChange={handleImageInputChange(insertImageAtCursor)}
              />
            </Box>
            <Paper
              ref={sourcePaneRef}
              onScroll={handleSourceScroll}
              variant="outlined"
              className="dq-flex-1 dq-min-h-0 dq-overflow-auto"
            >
              <CodeMirror
                value={body}
                options={{
                  mode: "markdown",
                  lineNumbers: true,
                  lineWrapping: true,
                  readOnly: lockedBy ? "nocursor" : false,
                }}
                editorDidMount={(editor) => {
                  codeMirrorRef.current = editor;
                }}
                onBeforeChange={(editor, data, value) =>
                  handleBodyChange(value)
                }
              />
            </Paper>
          </Box>
          {previewVisible && (
            <Box className="dq-flex-1 dq-min-h-0 dq-min-w-0 dq-flex dq-flex-col">
              <Box className="dq-flex dq-items-center dq-justify-between dq-mb-2 dq-flex-shrink-0">
                <Typography variant="subtitle2" color="text.secondary">
                  Preview
                </Typography>
                <Tooltip
                  title={
                    scrollSyncEnabled
                      ? "Turn off scroll sync"
                      : "Turn on scroll sync"
                  }
                >
                  <IconButton size="small" onClick={toggleScrollSync}>
                    {scrollSyncEnabled ? (
                      <SyncIcon fontSize="small" />
                    ) : (
                      <SyncDisabledIcon fontSize="small" />
                    )}
                  </IconButton>
                </Tooltip>
              </Box>
              <Paper
                ref={previewRef}
                onScroll={handlePreviewScroll}
                variant="outlined"
                className="article-preview dq-flex-1 dq-min-h-0 dq-overflow-auto dq-p-4"
              >
                <ReactMarkdown
                  children={body}
                  remarkPlugins={[remarkGfm]}
                  rehypePlugins={[rehypeRaw]}
                  sourcePos
                  components={{
                    img: ({ node, src, ...rest }) => (
                      <img
                        src={resolveImageSrc(src, meta.r2_public_url)}
                        {...rest}
                      />
                    ),
                  }}
                />
              </Paper>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}
